import { provider } from "@/provider.js"
import Vue from "vue"
import Helpers from "@/helpers.js"

import { subscribeToCaseChannel, unsubscribeToCaseChannel, subscribeToCaseChannelV2, unsubscribeToCaseChannelV2 } from "@/common/common_actions.js"

const removeSummaryFields = (caseData) => {
  for (const index of [1, 2, 3, 4, 5]) {
    delete caseData[`summaryVal${index}`]
    delete caseData[`summaryVal${index}Defn`]
  }
  return caseData
}

const ensureRecipientObject = (recipient) => {
  if (recipient && typeof recipient === "string") {
    return { displayName: recipient, email: recipient, type: "Custom", id: 0 }
  } else {
    // For User and Team types, the API expects id to be userId or teamId, not the recipient's database id
    let id = recipient.id
    if (recipient.type === "User" && recipient.userId !== undefined) {
      id = recipient.userId
    } else if (recipient.type === "Team" && recipient.teamId !== undefined) {
      id = recipient.teamId
    }
    return { displayName: recipient.displayName, email: recipient.email, type: recipient.type, id }
  }
}

export default {
  subscribeToCaseChannel,
  unsubscribeToCaseChannel,
  subscribeToCaseChannelV2,
  unsubscribeToCaseChannelV2,
  setCurrentCaseViewers({ commit }, viewers) {
    commit("setCurrentCaseViewers", viewers)
  },
  async getCurrentlySelectedCase({ commit }, data) {
    commit("requestingCase")
    const received = await provider.findCase(data.caseId)
    if (received.case) {
      commit("setCurrentCaseTemplate", {
        caseTypeId: received.case.caseTypeId,
      })
      commit("setCurrentlySelectedCase", received.case)
      commit("receivedCase")
    }
  },
  async getCurrentlySelectedCaseProperties({ commit }, data) {
    const received = await provider.findCase(data.caseId)
    if (received.case) {
      commit("updateCaseProperties", received.case.properties)
    }
  },
  async getCaseSummaryById(_, data) {
    return await provider.findCaseSummary(data.caseId)
  },
  async getCaseById(_, data) {
    return await provider.findCase(data.caseId)
  },
  async loadCaseConversationsPage({ commit }, { caseId, page = 1, pageSize = 100, append = false }) {
    const result = await provider.getCaseConversationsPage(caseId, page, pageSize)
    const payload = {
      conversations: result && result.conversations ? result.conversations : [],
      conversationCount: result && typeof result.total === "number" ? result.total : 0,
      page: result && result.page ? result.page : page,
      pageSize: result && result.pageSize ? result.pageSize : pageSize,
      hasMore: result && result.hasMore ? result.hasMore : false
    }
    if (append) {
      commit("appendCurrentCaseConversations", payload)
    } else {
      commit("setCurrentCaseConversations", payload)
    }
    return result
  },
  async getCurrentlySelectedCaseTimeline({ commit, rootState }, { caseId }) {
    const prefs = rootState.currentUser.me.preferences.timeline

    const criteria = {
      page: 0,
      pageSize: 10,
      showConversations: prefs.conversations,
      showCreated: prefs.created,
      showDocuments: prefs.documents,
      showParticipants: prefs.participants,
      showStateChanges: prefs.stateChanges,
      showTasks: prefs.tasks,
      showUpdates: prefs.updates,
      showViewed: prefs.viewed,
      showNotes: prefs.notes,
    }

    const timelineDays = await provider.findCaseTimeline(caseId, criteria)
    Helpers.adjustTimelineCaseTypeDisplayName(rootState, timelineDays)
    commit("setCurrentTimeline", timelineDays)
  },
  async getCaseSearch(_, data) {
    const searchResults = await provider.getCaseSearch(data.caseType, data.page, data.pageSize, data.query, data.fieldsToReturn)
    return searchResults
  },
  async addParticipantToCase({ commit }, { participant, type, caseId }) {
    if (participant) {
      const result = await provider.addCaseMember(caseId, participant.id, type)
      if (result.addCaseParticipant.error) {
        commit("displayFlashMessage", {
          message: result.addCaseParticipant.message,
          severity: "error",
        })
      }
    }
  },
  async removeParticipantFromCase({ commit }, { participant, type, caseId }) {
    if (participant) {
      const result = await provider.removeCaseMember(caseId, participant.id, type)
      if (result.removeCaseParticipant.error) {
        commit("displayFlashMessage", {
          message: result.removeCaseParticipant.message,
          severity: "error",
        })
      }
    }
  },
  async getBucketCases({ commit }, { bucketId, page, pageSize }) {
    commit("requestingCases")
    const received = await provider.findCases(bucketId, page, pageSize)
    commit("receivedCases")
    commit("setCases", received.bucket.contents.cases)
  },
  async updateCase({ commit }, caseData) {
    commit("savingCase")
    caseData = removeSummaryFields(caseData)

    const response = await provider.updateCase(caseData)
    commit("savedCase")
    if (response.error) {
      // Check if this is a schema field mismatch error
      if (response.invalidFields && response.invalidFields.length > 0) {
        // Show dialog asking user if they want to proceed
        commit("showInvalidFieldsDialog", {
          invalidFields: response.invalidFields,
          pendingCaseData: caseData
        })
      } else {
        const message = `${response.message} ${response.lowLevelErrorMessage || ""}`
        commit("displayFlashMessage", { message, severity: "error" })
      }
    } else {
      commit("setCurrentlySelectedCase", response.case)
      commit("displayFlashMessage", {
        message: response.message,
        severity: "success",
      })
    }
  },
  async updateCaseWithForce({ commit }, caseData) {
    commit("savingCase")
    caseData = removeSummaryFields(caseData)
    // Add force flag to remove invalid fields
    caseData.force = true

    const response = await provider.updateCase(caseData)
    commit("savedCase")
    commit("hideInvalidFieldsDialog")
    if (response.error) {
      const message = `${response.message} ${response.lowLevelErrorMessage || ""}`
      commit("displayFlashMessage", { message, severity: "error" })
    } else {
      commit("setCurrentlySelectedCase", response.case)
      commit("displayFlashMessage", {
        message: response.message,
        severity: "success",
      })
    }
  },
  async createCaseConversation({ commit }, conversation) {
    const result = await provider.createCaseConversation(conversation)
    if (result.createConversation.error) {
      commit("displayFlashMessage", {
        message: result.createConversation.message,
        severity: "error",
      })
    } else {
      commit("displayFlashMessage", {
        message: "Message sent.",
        severity: "success",
      })
    }
    return result
  },

  async archiveMessage({ commit }, { messageId, markArchived }) {
    const result = await provider.archiveMessage(messageId, markArchived)
    commit("displayFlashMessage", { message: result.message, severity: result.error ? "error" : "success" })
    return !result.error
  },

  async resendConversationMessage({ commit }, { messageId }) {
    const response = await provider.resendConversationMessages([messageId])
    if (!response) {
      commit("displayFlashMessage", { message: "Unable to retry message send.", severity: "error" })
      return false
    }
    const severity = response.error ? "error" : "success"
    const message = response.message || (response.error ? "Unable to retry message send." : "Message resend queued.")
    if (response.lowLevelErrorMessage && response.error) {
      commit("displayFlashMessage", { message: `${message} ${response.lowLevelErrorMessage}`, severity })
    } else {
      commit("displayFlashMessage", { message, severity })
    }
    return !response.error
  },

  async markConversationAsRead({ commit }, { conversationId, markRead }) {
    const result = await provider.markConversationAsRead(conversationId, markRead)
    if (result.error) {
      commit("displayFlashMessage", { message: result.message, severity: "error" })
    } else {
      commit("displayFlashMessage", { message: "Conversation Updated.", severity: "success" })
      commit("markConversationAsRead", { conversationId, markRead })
    }
    return !result.error
  },

  async attachConversationToCase({ commit }, { conversationId, caseId }) {
    if (!caseId) {
      commit("displayFlashMessage", { message: "Unable to attach conversation: missing case id.", severity: "error" })
      return
    }
    const result = await provider.attachConversationToCase(conversationId, caseId)
    if (result.error) {
      commit("displayFlashMessage", { message: result.message, severity: "error" })
    } else {
      commit("displayFlashMessage", { message: "Conversation Attached.", severity: "success" })
    }
  },
  async attachToCaseReturnToCommsroom({ commit, rootState }, { conversationId, caseId }) {
    if (!caseId) {
      commit("displayFlashMessage", { message: "Unable to attach conversation: missing case id.", severity: "error" })
      return
    }
    const result = await provider.attachConversationToCase(conversationId, caseId)
    if (result.error) {
      commit("displayFlashMessage", { message: result.message, severity: "error" })
    } else {
      commit("displayFlashMessage", { message: "Conversation Attached.", severity: "success" })
      Vue.router.push({
        name: "CommsInbox",
        params: { saved_search_id: rootState.ui.lastVisitedInboxId },
      })
    }
  },
  async removeConversationFromCase({ commit }, conversationId) {
    const result = await provider.removeConversationFromCase(conversationId)
    if (result.error) {
      commit("displayFlashMessage", { message: result.message, severity: "error" })
    } else {
      commit("displayFlashMessage", { message: "Conversation Removed.", severity: "success" })
    }
  },
  async sendMessage({ dispatch, rootState }, { message, recipMap }) {
    message.recipients = (recipMap.recipients || []).map((r) => ensureRecipientObject(r))
    message.ccRecipients = (recipMap.ccRecipients || []).map((r) => ensureRecipientObject(r))
    message.bccRecipients = (recipMap.bccRecipients || []).map((r) => ensureRecipientObject(r))

    if (message.selectedTemplateId) {
      const caseType = Helpers.getCaseTypeById(rootState, message.caseTypeId)
      if (caseType) {
        const template = caseType.emailTemplates.find((et) => parseInt(et.id, 10) === parseInt(message.selectedTemplateId, 10))
        message.templateWrapperId = template.templateWrapperId
      }
    }

    await dispatch("createCaseConversation", message)
    if (parseInt(rootState.cases.currentlySelected.case.id, 10) === parseInt(message.caseId, 10)) {
      dispatch("getCurrentlySelectedCase", { caseId: message.caseId })
    }
  },
  async replyCaseConversation({ commit }, { message, recipMap }) {
    message.recipients = (recipMap.recipients || []).map((r) => ensureRecipientObject(r))
    message.ccRecipients = (recipMap.ccRecipients || []).map((r) => ensureRecipientObject(r))
    message.bccRecipients = (recipMap.bccRecipients || []).map((r) => ensureRecipientObject(r))

    const result = await provider.replyCaseConversation(message)
    if (result.error) {
      commit("displayFlashMessage", {
        message: result.message,
        severity: "error",
      })
    } else {
      commit("displayFlashMessage", {
        message: "Reply sent.",
        severity: "success",
      })
      commit("replyCaseConversation", result)
    }
  },
  async forwardMessage({ commit }, { messageId, email }) {
    const result = await provider.forwardMessage(messageId, email)
    if (result.error) {
      commit("displayFlashMessage", { message: result.message, severity: "error" })
    } else {
      commit("displayFlashMessage", { message: result.message, severity: "success" })
    }
  },
  async deleteCases({ commit }, ids) {
    const result = await provider.deleteCases(ids)
    const severity = result && (result.error || result.failedCount > 0) ? "error" : "success"
    commit("displayFlashMessage", {
      message: result && result.message ? result.message : "Delete failed.",
      severity: severity,
    })
    return result && result.deletedCount > 0
  },
  async deleteBucketCases({ commit }, payload) {
    const result = await provider.deleteBucketCases(payload.bucketId, payload.confirmedCount, payload.searchField, payload.searchTerm)
    const severity = result && (result.error || result.failedCount > 0) ? "error" : "success"
    commit("displayFlashMessage", {
      message: result && result.message ? result.message : "Delete failed.",
      severity: severity,
    })
    return result && result.deletedCount > 0
  },
  async deleteSearchCases({ commit }, payload) {
    const result = await provider.deleteSearchCases(payload.caseTypeId, payload.searchTerm, payload.confirmedCount)
    const severity = result && (result.error || result.failedCount > 0) ? "error" : "success"
    commit("displayFlashMessage", {
      message: result && result.message ? result.message : "Delete failed.",
      severity: severity,
    })
    return result && result.deletedCount > 0
  },
  async deleteCase({ commit }, caseId) {
    const result = await provider.deleteCase(caseId)
    if (result.error) {
      commit("displayFlashMessage", {
        message: result.message,
        severity: "error",
      })
      return false
    } else {
      commit("displayFlashMessage", {
        message: "Case deleted.",
        severity: "success",
      })
      return true
    }
  },
  async createRelatedCase({ commit }, { caseTypeCode, properties }) {
    const response = await provider.createCase({ caseTypeCode, properties })
    if (response.createCase.error) {
      const message = `${response.createCase.message} ${response.createCase.lowLevelErrorMessage}`
      commit("displayFlashMessage", { message, severity: "error" })
    } else {
      commit("displayFlashMessage", {
        message: response.createCase.message,
        severity: "success",
      })
    }

    return response.createCase
  },
  async createCase({ commit }, data) {
    const response = await provider.createCase(data)
    if (response.createCase.error) {
      const message = `${response.createCase.message} ${response.createCase.lowLevelErrorMessage}`
      commit("displayFlashMessage", { message, severity: "error" })
    } else {
      commit("setCurrentlySelectedCase", response.createCase.caseData)
      commit("displayFlashMessage", {
        message: response.createCase.message,
        severity: "success",
      })
      commit("clearNewCaseRelations")
      Vue.router.push({
        name: "Case",
        params: { case_id: response.createCase.caseData.id },
      })
    }
  },
  async collectTasks({ commit }, tasks) {
    const idsStr = tasks.map((id) => `ids[]=${id}`).join("&")
    const data = await provider.getTasks(idsStr)
    commit("setTasks", data)
  },
  async createCaseTasklist({ commit }, tasklistData) {
    const response = await provider.createCaseTasklist(tasklistData)
    if (response.error) {
      commit("displayFlashMessage", {
        message: response.message,
        severity: "error",
      })
    } else {
      commit("addCaseTasklist", response)
    }
  },
  async updateCaseTasklist({ commit }, tasklistData) {
    const response = await provider.updateCaseTasklist(tasklistData)
    if (response.error) {
      commit("displayFlashMessage", {
        message: response.message,
        severity: "error",
      })
    } else {
      commit("updateCaseTasklist", response)
    }
  },
  async deleteCaseTasklist({ commit }, tasklistId) {
    const response = await provider.deleteCaseTasklist(tasklistId)
    if (response.error) {
      commit("displayFlashMessage", {
        message: response.message,
        severity: "error",
      })
    } else {
      commit("removeCaseTasklist", tasklistId)
    }
  },
  async createCaseTask({ commit }, taskData) {
    const response = await provider.createCaseTask(taskData)
    if (response.error) {
      commit("displayFlashMessage", {
        message: response.message,
        severity: "error",
      })
    } else {
      response.tasklistId = taskData.caseTasklistId
      commit("addCaseTask", response)
    }
  },
  async updateCaseTask({ commit }, taskData) {
    const response = await provider.updateCaseTask(taskData)
    if (response.error) {
      commit("displayFlashMessage", {
        message: response.message,
        severity: "error",
      })
    } else {
      commit("displayFlashMessage", {
        message: response.message,
        severity: "success",
      })
      commit("updateCaseTask", response)
    }
  },
  async deleteCaseTask({ commit }, taskId) {
    const response = await provider.deleteCaseTask(taskId)
    if (response.error) {
      commit("displayFlashMessage", {
        message: response.message,
        severity: "error",
      })
    } else {
      commit("removeCaseTask", taskId)
    }
  },
  async executeCaseTask({ commit }, taskId) {
    const response = await provider.executeCaseTask(taskId)
    if (response.error) {
      commit("displayFlashMessage", { message: response.message, severity: "error" })
      return false
    } else if (response.taskExecutionResult.result === "failed") {
      commit("displayFlashMessage", { message: response.taskExecutionResult.message, severity: "error" })
    } else {
      commit("displayFlashMessage", { message: response.taskExecutionResult.message, severity: "success" })

      if (taskId !== response.taskExecutionResult.taskId) {
        const update = {
          oldId: taskId,
          newId: response.taskExecutionResult.taskId,
        }
        commit("updateCaseTaskId", update)
      }
      let currentStatus = "not_started"
      if (response.error) {
        currentStatus = "failed"
      } else {
        if (response.taskExecutionResult.message.includes("succeeded") || response.taskExecutionResult.message.includes(" checked")) {
          currentStatus = "completed"
        }
      }
      commit("updateCaseTaskStatus", {
        taskId: response.taskExecutionResult.taskId,
        currentStatus,
      })
      return true
    }
  },
  async uploadCaseDocument({ commit, rootState }, data) {
    const path = `/documents/${data.user.account.id}/${data.case.caseTypeId}/${data.case.id}`
    const currentFolderPath = rootState.folders && rootState.folders.documentsTable && rootState.folders.documentsTable.currentFolderPath ? rootState.folders.documentsTable.currentFolderPath : "/"
    const url = window.location.href.startsWith("http://localhost") ? `http://localhost:6543${path}` : path
    const auth = "Bearer " + window.localStorage.session
    for (let i = 0; i < data.files.length; i++) {
      const formData = new FormData()
      formData.append("file", data.files[i])
      formData.append("case_folder_path", currentFolderPath)
      await fetch(url, {
        method: "POST",
        body: formData,
        headers: { Authorization: auth },
      })
    }
    const files = await provider.findCaseAttachments(data.case.id)
    commit("setCurrentCaseAttachments", { attachments: files })
    if (data.files[0]) {
      return data.files[0].name
    }
  },
  async copyCaseDocument({ commit, dispatch }, data) {
    const result = await provider.duplicateFileAttachment(data)
    if (!result.error) {
      commit("displayFlashMessage", {
        message: result.message,
        severity: "success",
      })
      if (result.fileAttachment) {
        commit("updateCaseFileAttachment", result.fileAttachment)
        // Navigate document table to location of copied file
        dispatch("documentTableNavigate", data.newFolderLocation)
      }
      commit("clearFolderSelection")
      commit("setCopyFileDialog", false)
      commit("setDuplicateFileDialog", false)
    } else {
      commit("displayFlashMessage", {
        message: result.lowLevelErrorMessage || result.message,
        severity: "error",
      })
    }
    return !result.error
  },
  async moveCaseDocument({ commit, dispatch, rootState }, data) {
    const result = await provider.moveFileAttachment(data)
    if (!result.error) {
      commit("displayFlashMessage", {
        message: result.message,
        severity: "success",
      })
      if (result.fileAttachment) {
        commit("updateCaseFileAttachment", result.fileAttachment)
        // Navigate document table to location of moved file
        if (!data.isFileRename) dispatch("documentTableNavigate", data.newFolderLocation)
      }
      commit("clearFolderSelection")
      commit("setMoveFileDialog", false)
      commit("setRenameFileDialog", false)
    } else {
      commit("displayFlashMessage", {
        message: result.message,
        severity: "error",
      })
    }
    return !result.error
  },
  async deleteCaseDocument({ commit, dispatch }, data) {
    const result = await provider.deleteFileAttachment(data.fileAttachmentId)
    if (!result.error) {
      commit("displayFlashMessage", {
        message: result.message,
        severity: "success",
      })
      commit("deleteCaseFileAttachment", data.fileAttachmentId)
      commit("clearFolderSelection")
      commit("setDeleteFileDialog", false)
    } else {
      commit("displayFlashMessage", {
        message: result.message,
        severity: "error",
      })
    }
  },
  async hideFileAttachment({ commit }, data) {
    const result = await provider.hideFileAttachment(data)
    if (!result.error) {
      commit("displayFlashMessage", {
        message: result.message,
        severity: "success",
      })
      console.log("before markCaseFileAttachmentHidden", data)
      commit("markCaseFileAttachmentHidden", data.id)
    } else {
      commit("displayFlashMessage", {
        message: result.message,
        severity: "error",
      })
    }
  },
  async showFileAttachment({ commit }, data) {
    const result = await provider.showFileAttachment(data)
    if (!result.error) {
      commit("displayFlashMessage", {
        message: result.message,
        severity: "success",
      })
      commit("markCaseFileAttachmentVisible", data.id)
    } else {
      commit("displayFlashMessage", {
        message: result.message,
        severity: "error",
      })
    }
  },

  checkCaseBookmark({ commit }, route) {
    const bookmarks = Vue.store.state.ui.caseWorkMenu[0].items[0].childItems
    let result = false
    bookmarks.forEach((bookmark) => {
      // Check both fullPath and path to match bookmarks with or without querystring
      if (bookmark.route === route || bookmark.route.split('?')[0] === route.split('?')[0]) {
        result = true
      }
    })
    commit("setCaseBookmarked", result)
  },
  async performGlobalSearch({ commit }, data) {
    commit("requestingBucket")
    const searchTerm = data && data.searchTerm ? data.searchTerm : data
    const page = data && typeof data.page === "number" ? data.page : 0
    const pageSize = data && typeof data.pageSize === "number" ? data.pageSize : 20
    const results = await provider.getGlobalSearch(searchTerm, page, pageSize)
    commit("receivedBucket")
    return results
  },
  async performCaseTypeSearch(_, data) {
    const result = await provider.getCaseTypeSearch(data.searchTerm, data.caseTypeId, data.page, data.pageSize, data.sortField, data.sortDirection)
    return result
  },

  async subscribeToCaseEvents({ commit }, data) {
    const results = await Promise.all(data.eventTypes.map((type) => provider.subscribeToCaseEvents(data.caseId, type)))
    let success = true
    results.forEach((result) => {
      if (result.error) {
        commit("displayFlashMessage", {
          message: result.message,
          severity: "error",
        })
        success = false
      }
    })
    return success
  },

  async unsubscribeFromCaseEvents({ commit }, data) {
    const results = await Promise.all(data.eventTypes.map((type) => provider.unsubscribeFromCaseEvents(data.caseId, type)))
    let success = true
    results.forEach((result) => {
      if (result.error) {
        commit("displayFlashMessage", {
          message: result.message,
          severity: "error",
        })
        success = false
      }
    })
    return success
  },

  addCaseNote({ commit }, author) {
    commit("addCaseNote", author)
  },

  async saveCaseNote({ commit, state }, { note, noteIndex }) {
    const caseId = state.currentlySelected.case.id
    try {
      let result
      if (note.id) {
        result = await provider.updateCaseNote(note)
      } else {
        result = await provider.createCaseNote(caseId, note)
      }
      if (result.error) {
        commit("displayFlashMessage", {
          message: `{result.message} ${result.lowLevelErrorMessage}`,
          severity: "error",
        })
      } else {
        commit("updateCaseNote", { updatedNote: result.caseNote, noteIndex })
        commit("displayFlashMessage", {
          message: `Case Note saved.`,
          severity: "success",
        })
      }
    } catch (error) {
      commit("displayFlashMessage", {
        message: `Error saving note: ${error.message}`,
        severity: "error",
      })
    }
  },

  async deleteCaseNote({ commit, state }, { noteIndex }) {
    const note = state.currentlySelected.case.notes[noteIndex]
    if (note.id) {
      try {
        const result = await provider.deleteCaseNote(note.id)
        if (!result.error) {
          commit("deleteCaseNote", { noteIndex })
          commit("displayFlashMessage", {
            message: `Case Note deleted.`,
            severity: "success",
          })
        } else {
          commit("displayFlashMessage", {
            message: `${result.message} ${result.lowLevelErrorMessage}`,
            severity: "error",
          })
        }
      } catch (error) {
        commit("displayFlashMessage", {
          message: `Error deleting note: ${error.message}`,
          severity: "error",
        })
      }
    }
  },
  editCaseNote({ commit }, editDetails) {
    commit("editCaseNote", editDetails)
  },
  prepareNewCaseRelations({ commit, state }) {
    const parentCase = {
      id: state.currentlySelected.case.id,
      caseTypeId: state.currentlySelected.case.caseTypeId,
      caseType: state.currentlySelected.case.caseType,
      title: state.currentlySelected.case.title,
    }
    commit("prepareNewCaseRelations", parentCase)
  },
  prepareNewCase({ commit, state, rootState }, { caseTypeId }) {
    const caseType = Helpers.getCaseTypeById(rootState, caseTypeId)
    commit("setCaseType", caseType)

    const newCase = {}
    newCase.caseType = caseType.name
    newCase.schema = caseType.currentSchema

    let parentCaseType
    let parentCase
    if (state.currentlySelected && state.currentlySelected.parentCase) {
      parentCase = state.currentlySelected.parentCase
      parentCaseType = Helpers.getCaseTypeById(rootState, parentCase.caseTypeId)
    }

    const properties = {}
    if (caseType.currentSchema) {
      for (const fieldset of caseType.currentSchema.fieldsets) {
        for (const field of fieldset.fields) {
          switch (field.type) {
            case "boolean":
              properties[field.name] = false
              break
            case "date":
              properties[field.name] = undefined
              break
            case "entity":
              properties[field.name] = []
              break
            case "relation":
              if (parentCaseType && field.relation === parentCaseType.code) {
                properties[field.name] = {
                  id: parentCase.id,
                  properties: {
                    title: parentCase.title,
                  },
                }
              } else {
                properties[field.name] = {}
              }
              break
            case "document":
              properties[field.name] = {}
              break
            case "case_type_states":
              properties.current_state = caseType.statuses[0].state
              properties.current_state_id = caseType.statuses[0].id
              break
            default:
              // String, Number, Address
              properties[field.name] = null
          }
        }
      }
    }
    newCase.properties = properties
    commit("setCurrentlySelectedCase", newCase)
  },
  clearNewCaseRelations({ commit }) {
    commit("clearNewCaseRelations")
  },
  async caseFieldsUpdated({ commit, rootState }, { caseData, field, row, table }) {
    const caseType = rootState.caseTypes.current
    if (caseType && caseType.scripts) {
      for (const script of caseType.scripts) {
        if (script.name === "caseFieldsUpdated") {
          console.log("PRE", caseData.properties)
          const resultPayload = await provider.executeScript({ id: script.id, payload: { caseProperties: caseData.properties, field, row, table }, executePublished: true })
          if (!resultPayload.error && resultPayload.output) {
            const updatedCaseProperties = JSON.parse(resultPayload.output)
            console.log("POST", updatedCaseProperties)
            if (updatedCaseProperties) {
              commit("updateCaseProperties", updatedCaseProperties.caseProperties)
            }
            return
          } else {
            console.error("caseFieldsUpdated error", resultPayload)
          }
        }
      }
    }
  },
  addCaseViaBucketId({ rootState }, bucketGroupId) {
    for (const caseType of rootState.caseTypes.all) {
      for (const bucketGroup of caseType.bucketGroups) {
        if (bucketGroup.id === bucketGroupId) {
          Vue.router.push({
            name: "CaseNew",
            params: { case_type_id: caseType.id },
          })
          return
        }
      }
    }
  },
  addCaseViaCaseTypeId({ rootState }, caseTypeId) {
    for (const caseType of rootState.caseTypes.all) {
      if (caseType.id === caseTypeId) {
        Vue.router.push({
          name: "CaseNew",
          params: { case_type_id: caseType.id },
        })
        return
      }
    }
  },
  async showCaseTaskRunHistory({ state, rootState, commit }, caseTask) {
    const allCaseTypeIds = rootState.caseTypes.all.map((ct) => ct.id)
    const excludedCaseTypeIds = allCaseTypeIds.filter((id) => id !== state.currentlySelected.case.caseTypeId)
    const criteria = {
      showTasks: true,
      page: 0,
      pageSize: 100,
      excludedCaseTypeIds,
    }
    const eventDays = await provider.findCaseTimeline(state.currentlySelected.case.id, criteria)
    let caseTaskEvents = []
    for (const eventDay of eventDays) {
      const filteredEvents = eventDay.events.filter((e) => e.params && e.params.taskId === caseTask.id)
      caseTaskEvents = caseTaskEvents.concat(filteredEvents)
    }
    const taskType = caseTask.task.actionType === "ManualTask" ? "Manual" : "Automation"
    const history = caseTaskEvents.map((e) => {
      const logs = e.params && e.params.logs ? e.params.logs : undefined
      const success = e.params && !!e.params.success
      return { causedAt: new Date(e.createdAt), taskType, causedById: e.causedBy.id, causedBy: e.causedBy.displayName, logs, success }
    })
    commit("setCaseTaskRunHistory", history)
  },
  async resolveTaskFailures({ commit }, caseTask) {
    const result = await provider.resolveTaskFailures(caseTask.id)
    if (result.error) {
      commit("displayFlashMessage", {
        message: result.message,
        severity: "error",
      })
    } else {
      commit("displayFlashMessage", {
        message: result.message,
        severity: "success",
      })
      commit("resolveTaskFailures", caseTask)
    }
  },
  async getCaseFolders({ commit }, { caseId, parentFolderId, dialogDocumentsTable }) {
    try {
      const response = await provider.getCaseFolders(parseInt(caseId, 10), parentFolderId)
      if (response) {
        const mutation = dialogDocumentsTable ? "setDialogFolders" : "setCaseFolders"
        commit(mutation, { caseId, caseFolders: response })
        return response
      } else {
        commit("displayFlashMessage", { message: "Error fetching case folders", severity: "error" })
      }
    } catch (err) {
      console.log(err)
      commit("displayFlashMessage", { message: err, severity: "error" })
    }
  },
}
